'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { useAtom } from 'jotai'
import { CheckIcon, EyeIcon, EyeOffIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'

import { SubmitButton } from '@/components/submit-button'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { AccountStatusChecker } from '@/components/widget/account-status-checker'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { useCreateAccount, useUpdateAccount } from '@/service/account.hook'
import { apiCredentialsFormStateAtom } from '@/state/api-credentials'
import { AccountStatus } from '@/types/generated/common/account'
import { TradeVenue } from '@/types/generated/common/types'

const schema = z.object({
  apiKey: z.string().min(1),
  apiSecret: z.string().min(1),
  accountName: z.string().optional(),
})

type FormData = z.infer<typeof schema>

export function ApiCredentialsForm() {
  const tCommon = useTranslations('common')
  const t = useTranslations('settings')
  const [formState, setFormState] = useAtom(apiCredentialsFormStateAtom)
  const [isSuccess, setIsSuccess] = useState(false)
  const [showSecret, setShowSecret] = useState(false)

  const isEditMode = Boolean(formState.editingAccount)
  const selectedExchange =
    formState.editingAccount?.tradeVenue || formState.tradeVenue || TradeVenue.TRADE_VENUE_DERIBIT

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      apiKey: formState.apiKey,
      apiSecret: formState.apiSecret,
      accountName: formState.accountName,
    },
  })

  const queryClient = useQueryClient()
  const { mutateAsync: updateAccount } = useUpdateAccount({
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ['accounts'],
      })
      toast.success(data.message)
      resetForm()
      setIsSuccess(true)
    },
    onError: (error) => {
      toast.error(error.message || t('failedUpdateAccount'))
    },
  })
  const { mutateAsync: createAccount } = useCreateAccount({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['accounts'],
      })
      resetForm()
      setIsSuccess(true)
    },
    onError: (error) => {
      console.error(error.message)
    },
  })

  const resetForm = () => {
    form.reset({
      apiKey: '',
      apiSecret: '',
      accountName: '',
    })
    setFormState({
      apiKey: '',
      apiSecret: '',
      accountName: '',
      tradeVenue: TradeVenue.TRADE_VENUE_UNSPECIFIED,
      editingAccount: null,
    })
  }

  const onSubmit = async (formData: FormData) => {
    if (isEditMode && formState.editingAccount) {
      await updateAccount({
        accountId: formState.editingAccount.accountId,
        apiKey: formData.apiKey.trim(),
        apiSecret: formData.apiSecret.trim(),
        name: formData.accountName?.trim(),
      })
    } else {
      await createAccount({
        apiKey: formData.apiKey.trim(),
        apiSecret: formData.apiSecret.trim(),
        name: formData.accountName?.trim() || `Account ${Date.now()}`,
        tradeVenue: selectedExchange,
      })
    }
  }

  // Sync atom state to form when editingAccount changes (edit button clicked)
  useEffect(() => {
    if (formState.editingAccount) {
      form.reset({
        apiKey: formState.apiKey || '',
        apiSecret: formState.apiSecret || '',
        accountName: formState.editingAccount.name || '',
      })
      setIsSuccess(false)
    }
  }, [formState.editingAccount?.accountId])

  // Sync form values to atom on change (for persistence across remounts)
  useEffect(() => {
    const subscription = form.watch((values) => {
      setFormState((prev) => ({
        ...prev,
        apiKey: values.apiKey || '',
        apiSecret: values.apiSecret || '',
        accountName: values.accountName || '',
      }))
    })
    return () => subscription.unsubscribe()
  }, [form, setFormState])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="flex w-full flex-col gap-4"
    >
      <Card className="w-full bg-white/2 p-6">
        <CardHeader className="border-b-0 p-0 pb-6 font-sans text-base font-medium tracking-tight text-white/60">
          {t('apiKey')}
        </CardHeader>
        <CardContent className="flex w-full flex-col gap-8 p-0">
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-3"
            data-1p-ignore={false}
          >
            {/* Account Name Row */}
            <div className="flex items-center justify-between">
              <label className="font-sans text-sm tracking-tight text-white/60">
                {t('accountNameOptional')}
              </label>
              <Input
                type="text"
                placeholder={t('enterAccountName')}
                {...form.register('accountName')}
                variant="default"
                inputClassName="h-9 w-[200px] border-0 bg-white/5 placeholder:text-white/30"
                disabled={isSuccess}
              />
            </div>

            {/* API Key Row */}
            <div className="flex items-center justify-between">
              <label className="font-sans text-sm tracking-tight text-white/60">
                {t('apiKey')}
              </label>
              <Input
                type="text"
                placeholder={t('enterApiKey')}
                {...form.register('apiKey')}
                variant="default"
                inputClassName="h-9 w-[200px] border-0 bg-white/5 placeholder:text-white/30"
                disabled={isSuccess}
              />
            </div>

            {/* API Secret Row */}
            <div className="flex items-center justify-between">
              <label className="font-sans text-sm tracking-tight text-white/60">
                {t('apiSecret')}
              </label>
              <div className="relative w-50">
                <Input
                  type={showSecret ? 'text' : 'password'}
                  placeholder={t('enterApiSecret')}
                  {...form.register('apiSecret')}
                  variant="default"
                  data-1p-ignore={true}
                  inputClassName="h-9 w-full border-0 bg-white/5 pr-9 placeholder:text-white/30"
                  disabled={isSuccess}
                />
                <Button
                  aria-label={showSecret ? tCommon('hideSecret') : tCommon('showSecret')}
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute top-0 right-0 h-9 w-9 hover:bg-transparent disabled:bg-transparent"
                  onClick={() => setShowSecret(!showSecret)}
                  disabled={isSuccess}
                >
                  {showSecret ? (
                    <EyeIcon className="size-4 text-white/40" />
                  ) : (
                    <EyeOffIcon className="size-4 text-white/40" />
                  )}
                </Button>
              </div>
            </div>

            <Separator className="bg-foreground/5" />
            {formState.editingAccount && (
              <div className="flex w-full flex-col gap-4">
                <p className="text-foreground/60 font-sans text-base tracking-tight">
                  {t('status')}
                </p>
                <div className="flex items-center justify-between gap-2">
                  <p className="tracking-tight text-white">{t('currentStatus')}</p>
                  <AccountStatusChecker account={formState.editingAccount} exact />
                </div>
                {formState.editingAccount.status === AccountStatus.ACCOUNT_STATUS_SUSPENDED && (
                  <p className="text-chart-mint/80 text-sm">{t('reactivateAccountDescription')}</p>
                )}
              </div>
            )}
            <div className="mt-4 flex items-center justify-end gap-3">
              <Button
                variant="secondary"
                className="text-sm"
                onClick={(e) => {
                  e.preventDefault()
                  resetForm()
                }}
              >
                {t('cancel')}
              </Button>
              <SubmitButton
                disabled={!form.formState.isValid || form.formState.isSubmitting || isSuccess}
                isSuccessful={isSuccess}
                className={cn(['w-fit text-sm', isSuccess && 'opacity-60'])}
              >
                {isSuccess ? <CheckIcon className="text-white" /> : t('saveChanges')}
              </SubmitButton>
            </div>
          </form>
        </CardContent>
      </Card>
    </motion.div>
  )
}
